import { msg, t } from "@lingui/core/macro";
import { z } from "zod";
import { dateSchema, humanFormat } from "./date-and-time";
import { isSameDay, isToday, isTomorrow, isYesterday } from "date-fns";
import { i18n, type MessageDescriptor } from "@lingui/core";
import { siteSchema } from "./sites";
import { teamSchema } from "./patients";

export const medicalCompetences = [
  "Administrator",
  "Dietitian",
  "DistrictNurse",
  "DoctorAssistant",
  "MedicalDoctor",
  "MedicalSocialWorker",
  "NurseAssistant",
  "NursePractitioner",
  "AdvancedNursePractitioner",
  "OccupationalTherapist",
  "Pharmacist",
  "Physiotherapist",
  "Psychologist",
  "Support",
] as const;

export const medicalCompetenceSchema = z.enum(medicalCompetences);
export type IMedicalCompetence = z.infer<typeof medicalCompetenceSchema>;

export const orderedCompetences = [
  medicalCompetenceSchema.enum.NurseAssistant,
  medicalCompetenceSchema.enum.NursePractitioner,
  medicalCompetenceSchema.enum.MedicalDoctor,
  medicalCompetenceSchema.enum.AdvancedNursePractitioner,
  medicalCompetenceSchema.enum.DoctorAssistant,
  medicalCompetenceSchema.enum.DistrictNurse,
  medicalCompetenceSchema.enum.MedicalSocialWorker,
  medicalCompetenceSchema.enum.Psychologist,
  medicalCompetenceSchema.enum.Pharmacist,
  medicalCompetenceSchema.enum.Physiotherapist,
  medicalCompetenceSchema.enum.OccupationalTherapist,
  medicalCompetenceSchema.enum.Dietitian,
  medicalCompetenceSchema.enum.Support,
  medicalCompetenceSchema.enum.Administrator,
] as const;

export const medicalCompetenceDictionary: Record<
  IMedicalCompetence,
  {
    long: MessageDescriptor;
    short: MessageDescriptor;
  }
> = {
  Administrator: { long: msg`Administratör`, short: msg`Admin` },
  Dietitian: { long: msg`Dietist`, short: msg`Diet` },
  DistrictNurse: { long: msg`Distriktssköterska`, short: msg`Dsk` },
  DoctorAssistant: { long: msg`Läkarassistent`, short: msg`LA` },
  MedicalDoctor: { long: msg`Läkare`, short: msg`Läk` },
  MedicalSocialWorker: { long: msg`Kurator`, short: msg`Kur` },
  NurseAssistant: { long: msg`Undersköterska`, short: msg`Usk` },
  NursePractitioner: { long: msg`Sjuksköterska`, short: msg`Ssk` },
  AdvancedNursePractitioner: {
    long: msg`Avancerad sjuksköterska`,
    short: msg`Ask`,
  },
  OccupationalTherapist: { long: msg`Arbetsterapeut`, short: msg`Arb` },
  Pharmacist: { long: msg`Farmaceut`, short: msg`Farm` },
  Physiotherapist: { long: msg`Fysioterapeut`, short: msg`Fys` },
  Psychologist: { long: msg`Psykolog`, short: msg`Psyk` },
  Support: {
    long: msg`Support`,
    short: msg`Supp`,
  },
};

/**
 * Support is a competence a shift can be staffed as, and a competence an activity can
 * require, but it is not a medical title a person holds. On an employee it therefore
 * carries no meaning and is normalised to null here, at the boundary where employee
 * data enters the app, so no consumer has to know about the special case.
 *
 * Accepts null as input as well as output, so parsed employees can be re-parsed
 * (mocks feed generated, already-parsed data back through the schema).
 */
export const employeeCompetenceSchema = medicalCompetenceSchema
  .nullable()
  .transform((competence) =>
    competence === medicalCompetenceSchema.enum.Support ? null : competence,
  );
export type IEmployeeCompetence = z.infer<typeof employeeCompetenceSchema>;

export const medicalTitle = (
  probablyMedicalCompetence: unknown,
  { format }: { format: "long" | "short" },
) => {
  try {
    const parsedMedicalCompetence = medicalCompetenceSchema.parse(
      probablyMedicalCompetence,
    );
    return i18n._(medicalCompetenceDictionary[parsedMedicalCompetence][format]);
  } catch {
    return undefined;
  }
};

export const employee = z.object({
  id: z.number().int().positive(),
  matrixUserId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  competence: employeeCompetenceSchema,
  imageURL: z.string().nullable(),
  distinctionLabel: z.string().nullish(),
});
export const employees = z.array(employee);
export type IEmployee = z.infer<typeof employee>;

/** Employees without a medical competence sort last. */
export const compareEmployeesByCompetence = (
  a: Pick<IEmployee, "competence">,
  b: Pick<IEmployee, "competence">,
) => {
  if (a.competence === null) {
    return b.competence === null ? 0 : 1;
  }
  if (b.competence === null) {
    return -1;
  }
  return a.competence.localeCompare(b.competence);
};

const mapCountryCodeAndViewerToFormality = {
  ["patient"]: {
    ["SE"]: "informal",
    ["CH"]: "formal",
    ["LV"]: "formal",
  },
  ["colleague"]: {
    ["SE"]: "informal",
    ["CH"]: "informal",
    ["LV"]: "informal",
  },
  ["medoma-employee"]: {
    ["SE"]: "formal",
    ["CH"]: "formal",
    ["LV"]: "formal",
  },
} as const;

export const employeeName = (
  { firstName, lastName }: Pick<IEmployee, "firstName" | "lastName">,
  options:
    | // Used in most places, where locale and relationship impacts how to display the name
      // Often less formal, and can be used for a friendlier tone
      {
        employeeNameFormat?: never;
        countryCode: "SE" | "CH" | "LV";
        viewer: "patient" | "colleague" | "medoma-employee";
      }
    // Used in places where a full name is always expected, e.g. shift/employee management page
    // Can be useful to minimize risks for duplicates, for example.
    | { employeeNameFormat: "full" },
) => {
  if (
    "employeeNameFormat" in options &&
    options.employeeNameFormat === "full"
  ) {
    return `${firstName} ${lastName}`;
  }
  const detectedContext =
    mapCountryCodeAndViewerToFormality[options.viewer][options.countryCode];
  if (detectedContext === "informal") {
    if (!lastName) {
      return firstName;
    }
    return `${firstName} ${lastName.charAt(0)}`;
  }
  return `${firstName} ${lastName}`;
};

export const shiftTypeSchema = z.enum([
  "Admin",
  "Ambulating",
  "Center",
  "Doctor",
  "External",
  "InHouse",
  "Introduction",
]);
export type IShiftType = z.infer<typeof shiftTypeSchema>;
export const shiftTypesDictionary: Record<IShiftType, MessageDescriptor> = {
  Admin: msg`Admin`,
  Ambulating: msg`Ambulerande`,
  Center: msg`Koordinator`,
  Doctor: msg`Läkare`,
  External: msg`Extern`,
  InHouse: msg`Inne`,
  Introduction: msg`Inskolning`,
};
export const unusualShiftTypes: Record<IShiftType, boolean> = {
  Admin: true,
  Center: true,
  InHouse: true,
  Introduction: true,
  Ambulating: false,
  Doctor: false,
  External: false,
};
export const shiftProviderSchema = z.enum(["medoma", "tessa", "quinyx"]);

export const scheduledShiftSchema = z.object({
  id: z.number(),
  employee: employee.nullish(),
  startDateTime: dateSchema,
  endDateTime: dateSchema,
  competence: medicalCompetenceSchema,
  type: shiftTypeSchema,
  deleted: z.boolean(),
  site: siteSchema,
  team: teamSchema.transform((team) => (team.id === 1 ? null : team)).nullish(),
  provider: shiftProviderSchema,
});
export type IScheduledShift = z.infer<typeof scheduledShiftSchema>;
export const scheduledShiftsSchema = z.array(scheduledShiftSchema);

export const calculateAvailableShifts = ({
  scheduledShifts,
  assignedShiftIds,
}: {
  scheduledShifts: IScheduledShift[];
  assignedShiftIds: number[];
}) =>
  scheduledShifts.filter(({ id }) => {
    if (assignedShiftIds.includes(id)) {
      return false;
    }
    return true;
  });

export const shiftName = (
  { employee, competence }: Pick<IScheduledShift, "employee" | "competence">,
  options: { competenceFormat: "abbreviated" | "full" } &
    // Re-use options from employeeName function
    Parameters<typeof employeeName>[1],
) => {
  const { competenceFormat } = options;
  if (!employee) {
    return (
      i18n._(
        medicalCompetenceDictionary[competence][
          competenceFormat === "abbreviated" ? "short" : "long"
        ],
      ) + ` (${t`obemannad`})`
    );
  }

  return employeeName(employee, options);
};

export const shiftTimeDisplayValues = ({
  selectedDate,
  startDateTime,
  endDateTime,
}: {
  selectedDate: Date;
  startDateTime: Date;
  endDateTime: Date;
}) => {
  // MED-1618: We know that we only have shifts that started on the selected day, or the day before.
  const startedDayBefore = !isSameDay(selectedDate, startDateTime);
  // MED-2431: Clear indication on nightshift that ends the day after.
  const hasEndTimeAfterToday = !isSameDay(startDateTime, endDateTime);
  const formattedDayBefore = (
    isYesterday(startDateTime)
      ? t`igår`
      : humanFormat(startDateTime, { month: "short", day: "numeric" })
  ).toLocaleLowerCase();
  const formattedDayToday = (
    isToday(startDateTime)
      ? t`idag`
      : humanFormat(startDateTime, { month: "short", day: "numeric" })
  ).toLocaleLowerCase();

  const formattedStartDay = (
    isToday(startDateTime)
      ? t`idag`
      : isYesterday(startDateTime)
        ? t`igår`
        : isTomorrow(startDateTime)
          ? t`imorgon`
          : humanFormat(startDateTime, { month: "short", day: "numeric" })
  ).toLocaleLowerCase();

  const formattedEndDay = (
    isToday(endDateTime)
      ? t`idag`
      : isYesterday(endDateTime)
        ? t`igår`
        : isTomorrow(endDateTime)
          ? t`imorgon`
          : humanFormat(endDateTime, { month: "short", day: "numeric" })
  ).toLocaleLowerCase();

  return {
    startedDayBefore,
    hasEndTimeAfterToday,
    formattedDayBefore,
    formattedDayToday,
    formattedEndDay,
    formattedStartDay,
  };
};

export const shiftPresetSchema = z.object({
  id: z.number().int().positive(),
  startTime: z.iso.time(),
  endTime: z.iso.time(),
  competence: medicalCompetenceSchema,
  shiftType: shiftTypeSchema.nullish(),
});

export type ShiftPreset = z.infer<typeof shiftPresetSchema>;
